/* OntologyPanel — fabrique de panneaux ContextStack pilotés par l'API générique
 * /workspace/context/{otype}/{oid}. Un clic sur une relation empile le panneau
 * suivant : Objet → Relation → Objet → Preuve. Réutilisable partout dans NOAM. */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, ChevronRight, MessageCircle, FileSearch, Building2, User, Handshake, ShieldAlert, ClipboardCheck, Boxes, CalendarCheck, Briefcase } from "lucide-react";

const TYPE_META = {
  entity: { icon: Building2, color: "#EFD127", label: "Entité" },
  person: { icon: User, color: "#378ADD", label: "Personne" },
  deal: { icon: Handshake, color: "#1D9E75", label: "Deal" },
  mandate: { icon: Briefcase, color: "#EFD127", label: "Mandat" },
  supplier: { icon: Boxes, color: "#7F77DD", label: "Fournisseur" },
  risk: { icon: ShieldAlert, color: "#E24B4A", label: "Risque" },
  control: { icon: ClipboardCheck, color: "#378ADD", label: "Contrôle" },
  execution: { icon: CalendarCheck, color: "#1D9E75", label: "Exécution" },
  event: { icon: CalendarCheck, color: "#71717a", label: "Événement" },
};
const EPI = { OBSERVED: "#1D9E75", DERIVED: "#378ADD", INFERRED: "#EF9F27" };

function OntologyPanelContent({ otype, oid, push }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setData(null); setErr(null);
    api.get(`/workspace/context/${otype}/${oid}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Objet non navigable."));
  }, [otype, oid]);

  if (err) return <div className="text-xs text-zinc-500 italic">{err}</div>;
  if (!data) return <div className="flex items-center gap-2 text-zinc-500 text-sm py-6"><Loader2 className="w-4 h-4 animate-spin" /> Lecture des relations…</div>;

  const o = data.object;
  return (
    <div className="space-y-4" data-testid={`ontology-panel-${otype}`}>
      <button
        onClick={() => navigate(`/app/intelligence?intent=${encodeURIComponent(`Parle-moi de « ${o.label} » (${(TYPE_META[otype] || {}).label || otype}) : ce que tu observes, ses relations et ce qui mérite mon attention.`)}`)}
        className="btn-secondary text-[11px] px-2.5 py-1.5 rounded-lg flex items-center gap-1.5" data-testid="ontology-talk-to-noam">
        <MessageCircle className="w-3.5 h-3.5" /> Parler à NOAM de cet élément
      </button>

      {o.fields?.length > 0 && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px]">
          {o.fields.map((f) => (
            <div key={f.k} className="text-zinc-400">
              {f.k} : <span className="text-zinc-200">{String(f.v)}</span>{" "}
              {f.epistemic && (
                <span className="text-[8px] font-mono uppercase px-1 py-0.5 rounded border"
                  style={{ color: EPI[f.epistemic], borderColor: `${EPI[f.epistemic]}4d`, background: `${EPI[f.epistemic]}14` }}>
                  {{ OBSERVED: "Observé", DERIVED: "Dérivé", INFERRED: "Inféré" }[f.epistemic]}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {o.provenance && (
        <div className="rounded-lg border border-[#378ADD]/30 bg-[#378ADD]/10 p-3" data-testid="ontology-provenance">
          <div className="text-[9px] uppercase tracking-widest text-[#378ADD] mb-1 flex items-center gap-1.5">
            <FileSearch className="w-3 h-3" /> Donnée source (provenance vérifiée)
          </div>
          <div className="text-xs text-zinc-200 font-mono">
            {o.provenance.file}<br />Feuille « {o.provenance.sheet} » · Ligne {o.provenance.row}
          </div>
        </div>
      )}

      {(data.relations || []).length === 0 && (
        <div className="text-xs text-zinc-500 italic">Aucune relation connue de NOAM pour cet objet. Rien n'est inventé.</div>
      )}
      {(data.relations || []).map((rel) => (
        <div key={rel.kind}>
          <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-1.5">{rel.label} ({rel.items.length})</div>
          {rel.items.map((it) => {
            const meta = TYPE_META[it.otype] || TYPE_META.event;
            const Icon = meta.icon;
            const navigable = it.otype !== "event" && it.oid;
            return (
              <button key={`${it.otype}-${it.oid}`} disabled={!navigable}
                onClick={() => navigable && push(ontologyPanel(it.otype, it.oid, it.label))}
                data-testid={`ontology-rel-${it.otype}-${it.oid}`}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/5 enabled:hover:border-white/20 transition-colors mb-1 text-left disabled:opacity-70">
                <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: meta.color }} />
                <span className="text-xs text-zinc-200 flex-1 truncate">{it.label}</span>
                {it.sub && <span className="text-[10px] text-zinc-500 truncate max-w-[40%]">{it.sub}</span>}
                {navigable && <ChevronRight className="w-3.5 h-3.5 text-zinc-600 shrink-0" />}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* Fabrique de panneau ContextStack pour n'importe quel objet de l'ontologie. */
export function ontologyPanel(otype, oid, label) {
  const meta = TYPE_META[otype] || {};
  return {
    id: `${otype}-${oid}`,
    crumb: (label || meta.label || otype).slice(0, 26),
    title: label || meta.label || otype,
    subtitle: null,
    badge: (
      <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border"
        style={{ color: meta.color, borderColor: `${meta.color}4d`, background: `${meta.color}14` }}>
        {meta.label || otype}
      </span>
    ),
    render: (push) => <OntologyPanelContent otype={otype} oid={oid} push={push} />,
  };
}
